
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

/**
 * Hook for fetching and managing notification counts
 */
export const useNotificationsCounts = () => {
  const { user } = useAuth();
  const [activeCount, setActiveCount] = useState(0);
  const [archivedCount, setArchivedCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  // Get counts for active notifications
  const { data: activeNotificationsCount = 0 } = useQuery({
    queryKey: ['notifications-count', 'active'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('archived', false)
          .eq('is_deleted', false); // Added filter to exclude deleted notifications
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error("Error fetching active notifications count:", error);
        return 0;
      }
    },
    staleTime: 30000, // 30 seconds - rely on realtime updates
  });

  // Get counts for archived notifications
  const { data: archivedNotificationsCount = 0 } = useQuery({
    queryKey: ['notifications-count', 'archived'],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('archived', true)
          .eq('is_deleted', false); // Added filter to exclude deleted notifications
        
        if (error) throw error;
        return count || 0;
      } catch (error) {
        console.error("Error fetching archived notifications count:", error);
        return 0;
      }
    },
    staleTime: 30000, // 30 seconds - rely on realtime updates
  });

  // Get count for unread notifications
  const { data: unreadNotificationsCount = 0 } = useQuery({
    queryKey: ['notifications-count', 'unread'],
    queryFn: async () => {
      if (!user?.id) return 0;

      try {
        // Eerst halen we alle actieve meldingen op
        const { data: activeNotifications, error: activeError } = await supabase
          .from('notifications')
          .select('id')
          .eq('archived', false)
          .eq('is_deleted', false); // Added filter to exclude deleted notifications

        if (activeError) throw activeError;

        if (!activeNotifications?.length) return 0;

        // Dan halen we alle gelezen statussen op voor deze gebruiker
        const { data: readStatuses, error: readError } = await supabase
          .from('notification_read_status')
          .select('notification_id')
          .eq('user_id', user.id)
          .eq('is_read', true);

        if (readError) throw readError;

        // Maak een set van gelezen meldingen voor snelle lookup
        const readNotificationIds = new Set(readStatuses?.map(status => status.notification_id) || []);

        // Tel hoeveel actieve meldingen niet in de gelezen set zitten
        const unreadCount = activeNotifications.filter(
          notification => !readNotificationIds.has(notification.id)
        ).length;

        return unreadCount;
      } catch (error) {
        console.error("Error fetching unread notifications count:", error);
        return 0;
      }
    },
    staleTime: 30000, // 30 seconds - rely on realtime updates
  });

  // Update local state when counts change
  useEffect(() => {
    setActiveCount(activeNotificationsCount);
  }, [activeNotificationsCount]);

  useEffect(() => {
    setArchivedCount(archivedNotificationsCount);
  }, [archivedNotificationsCount]);

  useEffect(() => {
    setUnreadCount(unreadNotificationsCount);
  }, [unreadNotificationsCount]);

  return {
    activeNotificationsCount: activeCount,
    archivedNotificationsCount: archivedCount,
    unreadNotificationsCount: unreadCount
  };
};
